/**
 * QuickBooks Upload History Component
 *
 * Displays past sales receipt uploads with details and results.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import {
  useGetUploadHistoryQuery,
  useUndoUploadMutation,
} from '../../../api/quickbooksApi';
import type { UploadResults } from '../../../types/quickbooks';
import { useSuccessNotification } from '../../../hooks/useSuccessNotification';

interface UploadHistoryProps {
  studioId: string;
  latestResults: UploadResults | null;
}

const UploadHistory: React.FC<UploadHistoryProps> = ({
  studioId,
  latestResults,
}) => {
  const {
    data: uploadHistory,
    isLoading,
    error,
  } = useGetUploadHistoryQuery(studioId);

  const [undoUpload, { isLoading: isUndoing }] = useUndoUploadMutation();
  const { showSuccess } = useSuccessNotification();

  // Error state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Undo confirmation dialog state
  const [undoDialog, setUndoDialog] = useState<{
    open: boolean;
    uploadId: string | null;
    fileName: string;
    receiptCount: number;
  }>({
    open: false,
    uploadId: null,
    fileName: '',
    receiptCount: 0,
  });

  // Error details dialog state
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean;
    fileName: string;
    uploadDate: string;
    errors: Array<{
      customer_email?: string;
      txn_date?: string;
      total_amount?: number;
      payment_type?: string;
      error?: string;
      account_code?: string;
      memo?: string;
      student?: string;
    }>;
  }>({
    open: false,
    fileName: '',
    uploadDate: '',
    errors: [],
  });

  const handleErrorsClick = (
    fileName: string,
    uploadDate: string,
    errors: Array<{ customer: string; error: string; receipt?: any }>
  ) => {
    setErrorDialog({
      open: true,
      fileName,
      uploadDate,
      errors,
    });
  };

  const handleErrorDialogClose = () => {
    setErrorDialog({
      open: false,
      fileName: '',
      uploadDate: '',
      errors: [],
    });
  };

  const handleUndoClick = (
    uploadId: string,
    fileName: string,
    receiptCount: number
  ) => {
    setUndoDialog({
      open: true,
      uploadId,
      fileName,
      receiptCount,
    });
  };

  const handleUndoConfirm = async () => {
    if (!undoDialog.uploadId) return;

    try {
      const result = await undoUpload({
        studioId,
        uploadId: undoDialog.uploadId,
      }).unwrap();

      const message = `Undo completed: ${result.deleted_count} receipt(s) deleted from QuickBooks${
        result.failed_count > 0
          ? `, ${result.failed_count} failed to delete`
          : ''
      }`;

      showSuccess({
        message,
        successType: 'general',
        hideDelay: 5000,
      });

      setUndoDialog({
        open: false,
        uploadId: null,
        fileName: '',
        receiptCount: 0,
      });
    } catch (err: any) {
      setErrorMessage(
        err?.data?.error || 'Failed to undo upload. Please try again.'
      );
      setUndoDialog({
        open: false,
        uploadId: null,
        fileName: '',
        receiptCount: 0,
      });
    }
  };

  const handleUndoCancel = () => {
    setUndoDialog({
      open: false,
      uploadId: null,
      fileName: '',
      receiptCount: 0,
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>Loading upload history...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load upload history. Please try again.
      </Alert>
    );
  }

  if (!uploadHistory || uploadHistory.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No Upload History Yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your first sales receipt file to see the history here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Error Alert */}
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{ mb: 3 }}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Latest Results Alert */}
      {latestResults && (
        <Alert
          severity={latestResults.failed_count === 0 ? 'success' : 'warning'}
          sx={{ mb: 3 }}
        >
          <Typography variant="body1" gutterBottom>
            <strong>Latest Upload Complete</strong>
          </Typography>
          <Typography variant="body2">
            Successfully uploaded {latestResults.successful_count} receipts.
            {latestResults.failed_count > 0 &&
              ` ${latestResults.failed_count} failed - see details below.`}
          </Typography>
        </Alert>
      )}

      {/* Upload History Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>File Name</TableCell>
              <TableCell>Uploaded By</TableCell>
              <TableCell>Payment Types</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Success</TableCell>
              <TableCell align="right">Failed</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uploadHistory.map(upload => {
              // Determine status color
              let statusColor: 'success' | 'error' | 'warning' = 'warning';
              if (
                upload.status === 'completed' &&
                upload.failed_uploads === 0
              ) {
                statusColor = 'success';
              } else if (upload.status === 'failed') {
                statusColor = 'error';
              }

              return (
                <TableRow key={upload.id}>
                  <TableCell>
                    {new Date(upload.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>{upload.file_name}</TableCell>
                  <TableCell>
                    {upload.uploaded_by.first_name}{' '}
                    {upload.uploaded_by.last_name}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {upload.payment_types.map(type => (
                        <Chip key={type} label={type} size="small" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {upload.total_transactions}
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <SuccessIcon fontSize="small" color="success" />
                      {upload.successful_uploads}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {upload.failed_uploads > 0 ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ErrorIcon fontSize="small" color="error" />
                        {upload.failed_uploads}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={upload.status.toUpperCase()}
                      size="small"
                      color={statusColor}
                    />
                  </TableCell>
                  <TableCell>
                    {upload.upload_summary && upload.failed_uploads > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<ErrorIcon />}
                        onClick={() =>
                          handleErrorsClick(
                            upload.file_name,
                            new Date(upload.created_at).toLocaleString(),
                            upload.upload_summary.failed
                          )
                        }
                      >
                        View Errors ({upload.failed_uploads})
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {upload.is_undone && (
                      <Tooltip
                        title={`Undone on ${new Date(
                          upload.undone_at!
                        ).toLocaleString()} by ${
                          upload.undone_by?.first_name
                        } ${upload.undone_by?.last_name}`}
                      >
                        <Chip
                          label="Undone"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {!upload.is_undone && upload.can_undo && (
                      <Tooltip title="Delete all receipts from QuickBooks">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleUndoClick(
                              upload.id,
                              upload.file_name,
                              upload.successful_uploads
                            )
                          }
                          disabled={isUndoing}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!upload.is_undone && !upload.can_undo && (
                      <Tooltip title="No receipts to undo">
                        <span>
                          <IconButton size="small" disabled>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Error Details Dialog */}
      <Dialog
        open={errorDialog.open}
        onClose={handleErrorDialogClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon color="error" />
            <Box>
              <Typography variant="h6">Upload Errors</Typography>
              <Typography variant="body2" color="text.secondary">
                {errorDialog.fileName} - {errorDialog.uploadDate}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            <Typography variant="body2">
              <strong>{errorDialog.errors.length}</strong> receipt(s) failed to
              upload. Review the errors below to resolve the issues.
            </Typography>
            {errorDialog.errors.length > 0 &&
              !errorDialog.errors[0].student &&
              !errorDialog.errors[0].memo &&
              !errorDialog.errors[0].account_code && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Note: This is historical error data. Transaction details may
                  not be available for older uploads.
                </Typography>
              )}
          </Alert>

          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { py: 1.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Memo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>
                    Failure Reason
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorDialog.errors.map((fail, index) => (
                  <TableRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`error-${index}-${(fail.customer_email || 'unknown').replace(/\s/g, '')}-${(fail.error || '').substring(0, 30).replace(/\s/g, '')}`}
                    sx={{
                      '&:hover': { bgcolor: 'grey.50' },
                      bgcolor: index % 2 === 0 ? 'white' : 'grey.50',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {fail.student || '-'}
                    </TableCell>
                    <TableCell>{fail.txn_date || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        {fail.memo || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{fail.account_code || '-'}</TableCell>
                    <TableCell>{fail.payment_type || '-'}</TableCell>
                    <TableCell align="right">
                      {fail.total_amount !== undefined
                        ? `$${Number(fail.total_amount).toFixed(2)}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ fontWeight: 'medium' }}
                      >
                        {fail.error || 'Unknown error'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Undo Confirmation Dialog */}
      <Dialog open={undoDialog.open} onClose={handleUndoCancel}>
        <DialogTitle>Undo Upload?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete all <strong>{undoDialog.receiptCount}</strong>{' '}
            receipt(s) from <strong>{undoDialog.fileName}</strong> in
            QuickBooks.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            This action cannot be undone. You will need to re-upload the file if
            you want to restore these receipts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUndoCancel} disabled={isUndoing}>
            Cancel
          </Button>
          <Button
            onClick={handleUndoConfirm}
            color="error"
            variant="contained"
            disabled={isUndoing}
            startIcon={
              isUndoing ? <CircularProgress size={16} /> : <UndoIcon />
            }
          >
            {isUndoing ? 'Deleting...' : 'Delete Receipts'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadHistory;
